import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react'

import { ApiError, api, type CurrentUser, type Permission } from './api'

/**
 * The session, and what this operator may do.
 *
 * Permissions come from the **active membership**, never from the user row — P027 moved the role
 * onto the membership so that the same person can hold different standing in two organizations.
 * A `can()` that read the user would silently grant one organization's rights inside another.
 *
 * `can()` fails closed. No membership, or a permission absent from the list, means no. The screen
 * is not the control — every one of these is enforced again in the API — but a UI that offers an
 * action it cannot complete is worse than one that hides it.
 */

interface Session {
  user: CurrentUser | null
  loading: boolean
  /** Organization-plane permission, from the active membership. */
  can: (permission: Permission) => boolean
  /** Axmion-staff standing. Separate plane; an org role never grants it. */
  isPlatformStaff: boolean
  refresh: () => Promise<void>
}

const SessionContext = createContext<Session>({
  user: null,
  loading: true,
  can: () => false,
  isPlatformStaff: false,
  refresh: async () => {},
})

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const query = useQuery({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        const { user } = await api.get<{ user: CurrentUser }>('/auth/me')
        return user
      } catch (error) {
        // A 401 is the signed-out state, not a failure. Anything else is a real error and is
        // allowed to surface, so a broken API does not look like a logged-out session.
        if (error instanceof ApiError && error.isUnauthorized) return null
        throw error
      }
    },
    retry: false,
    staleTime: 30_000,
  })

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['session'] })
  }, [queryClient])

  const user = query.data ?? null

  const value = useMemo<Session>(
    () => ({
      user,
      loading: query.isLoading,
      can: (permission) => user?.membership?.permissions.includes(permission) ?? false,
      isPlatformStaff: (user?.platformRole ?? 'NONE') !== 'NONE',
      refresh,
    }),
    [user, query.isLoading, refresh],
  )

  return <SessionContext value={value}>{children}</SessionContext>
}

export function useSession() {
  return useContext(SessionContext)
}
